import { Component, OnDestroy, OnInit } from '@angular/core';
import { Router } from '@angular/router';
import {
  LocalStorageService,
  ToastService,
} from '@g99-product-web-ui/shared-service';
import {
  G99ColoredLogo,
  MfaThumbnail,
  get,
} from '@g99-product-web-ui/shared-util-lib';

import { IUserAuthenticationDetails } from '../../interface/IMfa.interface';
import { MultifactorAuthenticationService } from '../../services/multifactor-authentication.service';

@Component({
  selector: 'g99-mfa-setup',
  templateUrl: './mfa-setup.component.html',
  styleUrl: './mfa-setup.component.scss',
  standalone: false,
})
export class MfaSetupComponent implements OnInit, OnDestroy {
  protected countdown = 60; // Countdown timer value in seconds
  protected isResendButtonDisabled = true; // Disables the resend button during countdown
  protected selectedAuthenticationMethod!: string; // Selected MFA method (TOTP or SMS)
  protected otpAuthUrl = ''; // OTP Auth URL generated for TOTP setup
  protected errorMessage = ''; // Error message for user feedback
  protected accessToken = ''; // Access token for API calls
  protected isLoading = false; // Loading state for button and spinner
  protected completedMfaSetupMethod!: string; // Indicates the completed MFA method
  protected g99ColoredLogo = G99ColoredLogo;
  protected mfaThumbnail = MfaThumbnail;
  private _timerInterval: ReturnType<typeof setInterval> | null = null;

  constructor(
    private readonly _mfaSetupService: MultifactorAuthenticationService,
    private readonly _toastService: ToastService,
    private readonly _router: Router,
    private readonly _localStorageService: LocalStorageService
  ) {}

  /**
   * Lifecycle hook that runs when the component is initialized.
   * Sets up the countdown timer and retrieves the access token from local storage.
   */
  public ngOnInit(): void {
    this._initializeCountdown();
    this._retrieveAccessToken();
  }

  /**
   * Lifecycle hook that runs when the component is destroyed.
   * Clears the countdown timer to avoid memory leaks.
   */
  public ngOnDestroy(): void {
    this._clearCountdownTimer();
  }

  /**
   * Handles the selection of an authentication method by the user.
   * @param method - The authentication method selected by the user (e.g., 'totp', 'sms').
   */
  protected onAuthenticationMethodSelect(method: string): void {
    this.selectedAuthenticationMethod = method;
  }

  /**
   * Resets the selected authentication method, allowing the user to choose a different method.
   */
  protected onResetAuthenticationMethod(): void {
    this.completedMfaSetupMethod = '';
  }

  /**
   * Resets the countdown timer and enables the resend code functionality.
   */
  protected onResendCode(): void {
    this._initializeCountdown();
  }

  /**
   * Logs the user out by clearing local storage and redirects to the login page.
   */
  protected logoutAndRedirectToLogin(): void {
    this.isLoading = false;
    localStorage.clear();
    this._router.navigate(['/']);
  }

  /**
   * Verifies the selected authentication method and initiates the setup process.
   * If 'totp' is selected, starts TOTP setup. If 'sms' is selected, completes SMS setup.
   * Displays an error if no valid authentication method is selected.
   */
  protected onVerifyAuthenticationMethod(): void {
    switch (this.selectedAuthenticationMethod) {
      case 'totp':
        this._setupTotpAuthentication();
        break;
      case 'sms':
        this.completedMfaSetupMethod = 'sms';
        break;
      default:
        this._toastService.showError(
          'Please select a valid authentication method.'
        );
    }
  }

  /**
   * Starts a 60-second countdown timer during which the resend button is disabled.
   * Enables the resend button once the timer ends.
   */
  private _initializeCountdown(): void {
    /* TODO: Method is not in use added for future reference when we will implement SMS setup */
    return;
    this.isResendButtonDisabled = true;
    this.countdown = 60;

    this._timerInterval = setInterval(() => {
      this.countdown--;

      if (this.countdown <= 0) {
        this._clearCountdownTimer();
        this.isResendButtonDisabled = false;
      }
    }, 1000);
  }

  /**
   * Clears the countdown timer to prevent further execution and avoids memory leaks.
   */
  private _clearCountdownTimer(): void {
    /* TODO: Method is not in use added for future reference when we will implement SMS setup */
    if (this._timerInterval) {
      clearInterval(this._timerInterval);
      this._timerInterval = null;
    }
  }

  /**
   * Retrieves the access token from the local storage and assigns it to the `accessToken` property.
   * Uses lodash's `get` function to handle missing or undefined values safely.
   */
  private _retrieveAccessToken(): void {
    const sessionInfo = this._localStorageService.readItem('tempSessionInfo');
    this.accessToken = get(sessionInfo, 'accessToken', '');
  }

  /**
   * Initiates the setup process for Time-based One-Time Password (TOTP) authentication.
   * Calls the TOTP setup API and processes the response to generate the OTP Auth URL.
   * Handles errors during the setup process.
   */
  private _setupTotpAuthentication(): void {
    this.isLoading = true;
    this._mfaSetupService.initiateTotpSetup(this.accessToken).subscribe({
      next: (response: IUserAuthenticationDetails) => {
        this.otpAuthUrl = this._generateOtpAuthUrl(response);
        this.completedMfaSetupMethod = 'totp';
        this.isLoading = false;
      },
      error: (error) => this._handleTotpSetupError(error),
    });
  }

  /**
   * Generates the OTP Auth URL for TOTP authentication based on the response from the API.
   * @param data - The details required to construct the OTP Auth URL.
   * @returns The generated OTP Auth URL.
   */
  private _generateOtpAuthUrl(data: IUserAuthenticationDetails): string {
    return `otpauth://totp/${data.issuer}:${data.username}?secret=${data.secretCode}&issuer=${data.issuer}`;
  }

  /**
   * Handles errors that occur during the TOTP setup process.
   * Displays an error message to the user and stops the loading spinner.
   * @param error - The error object containing details about the failure.
   */
  private _handleTotpSetupError(error: { message?: string }): void {
    const errorMessage =
      error?.message || 'Failed to set up TOTP. Please try again.';
    this._toastService.showError(errorMessage);
    this.isLoading = false;
  }
}
